import React, { useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { LineChart as LineIcon } from "lucide-react";
import api from "@/lib/api";

const METRICS = [
  { key: "total_xp", label: "Total XP", color: "#FFCF3F" },
  { key: "total_level", label: "Total Level", color: "#00FF00" },
  { key: "combat_level", label: "Combat", color: "#00FFFF" },
];

const fmt = (ts) => {
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

export default function HistoryChart({ profileId, refreshKey }) {
  const [points, setPoints] = useState([]);
  const [metric, setMetric] = useState("total_xp");

  useEffect(() => {
    if (!profileId) return;
    api
      .get(`/profiles/${profileId}/history`)
      .then(({ data }) => setPoints(data))
      .catch(() => setPoints([]));
  }, [profileId, refreshKey]);

  const active = METRICS.find((m) => m.key === metric);
  const data = points.map((p) => ({ ts: fmt(p.ts), value: p[metric] }));

  return (
    <div className="osrs-panel p-4" data-testid="history-chart">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="osrs-title text-lg flex items-center gap-2">
          <LineIcon size={20} /> Progress Over Time
        </h2>
        <div className="flex gap-1">
          {METRICS.map((m) => (
            <button
              key={m.key}
              data-testid={`metric-${m.key}`}
              onClick={() => setMetric(m.key)}
              className={`osrs-btn px-3 py-1 text-xs ${metric === m.key ? "" : "opacity-60"}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {points.length < 2 ? (
        <div className="text-center py-10" data-testid="history-empty">
          <p className="osrs-shadow text-[#E6A519] text-sm">
            Not enough data yet. Hit <span className="text-[#FFCF3F]">Refresh</span> over the coming days to
            chart your gains — a new snapshot is recorded as your stats change.
          </p>
          {points.length === 1 && (
            <p className="osrs-num text-[#00FF00] osrs-shadow text-2xl mt-3">
              {Number(points[0][metric] || 0).toLocaleString()} <span className="text-sm text-[#E6A519]">{active.label}</span>
            </p>
          )}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#5a4a2a" strokeDasharray="3 3" />
            <XAxis dataKey="ts" tick={{ fill: "#E6A519", fontFamily: "VT323", fontSize: 14 }} stroke="#5a4a2a" />
            <YAxis
              tick={{ fill: "#E6A519", fontFamily: "VT323", fontSize: 14 }}
              stroke="#5a4a2a"
              width={60}
              tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
            />
            <Tooltip
              contentStyle={{ background: "#3a3428", border: "2px solid #0f0d09", color: "#FFCF3F", fontFamily: "VT323" }}
              labelStyle={{ color: "#00FFFF" }}
              formatter={(v) => [Number(v).toLocaleString(), active.label]}
            />
            <Line type="monotone" dataKey="value" stroke={active.color} strokeWidth={3} dot={{ fill: active.color, r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
